import { Combobox } from '@headlessui/react';
import { Input } from '@insify/design-system/components';
import { MapPin } from '@insify/design-system/icons';
import { textMd } from '@insify/design-system/typography';
import clsx from 'clsx';

import * as styles from './Autocomplete.module.css';

import type { InputProps } from '@insify/design-system/components';
import type React from 'react';
import type { SetRequired } from 'type-fest';

export type Props<T> = SetRequired<Omit<InputProps, 'value' | 'onSelect'>, 'onChange'> & {
  /** Search input ref */
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  value?: T;
  options: T[];
  onSelect: (option: T) => void;
  mapOptionToSuggestionLabel: (option: T) => string;
  mapOptionToKey?: (option: T) => string;
  positionSuggestions?: 'top' | 'bottom';
};

// https://headlessui.com/react/combobox
export const InputAutocomplete = <T,>({
  value,
  onSelect,
  disabled,
  inputRef,
  options,
  mapOptionToKey,
  mapOptionToSuggestionLabel,
  positionSuggestions = 'top',
  ...props
}: Props<T>) => {
  return (
    <Combobox value={value} onChange={onSelect} nullable={false} multiple={false} disabled={disabled}>
      <div className={styles.fieldWrapper}>
        <Combobox.Input<typeof Input, T>
          displayValue={(option) => (option !== undefined ? mapOptionToSuggestionLabel(option) : ``)}
          as={Input}
          ref={inputRef}
          {...props}
        />
        <Combobox.Options className={clsx(styles.listWrapper, styles[positionSuggestions])}>
          {options.map((option) => {
            return (
              <Combobox.Option key={(mapOptionToKey ?? mapOptionToSuggestionLabel)(option)} value={option}>
                {({ active }) => (
                  <button type='button' className={clsx(styles.suggestion, active && styles.active)}>
                    <MapPin className={styles.suggestionIcon} />
                    <span className={clsx(textMd, styles.suggestionText)}>{mapOptionToSuggestionLabel(option)}</span>
                  </button>
                )}
              </Combobox.Option>
            );
          })}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};
